import { useState } from 'react';
import { PageShell, Section } from '~/components/layout';
import { Grid, Icon } from '~/components/ui';
import { CtaBanner } from './CtaBanner';
import { CurrencyCalculator } from './CurrencyCalculator';
import { CurrencyRatesStrip } from './CurrencyRatesStrip';
import { DownloadQr } from './DownloadQr';
import { FaqAccordion } from './FaqAccordion';
import { FeatureBlock } from './FeatureBlock';
import { RateComparison } from './RateComparison';
import { StepsSection } from './StepsSection';
import { TestimonialGrid } from './TestimonialGrid';
import { TextSection } from './TextSection';
import { compareSteps } from '~/content/compare';
import { sendMoneyDownload, sendMoneyHeroFeatures } from '~/content/sendMoneyHome';
import { safeSpaceSection } from '~/content/shared';
import type { PricingTier, ReceiveCurrency } from '~/lib/quote';

export interface SendMoneyCalculatorPageProps {
  /** Hero H1 (supports `\n` via whitespace-pre-line). */
  heroTitle: string;
  /** Initial receive currency (from route slug or send-money default). */
  initialTarget: ReceiveCurrency;
}

/**
 * Shared send-money / calculator landing layout: hero + FX calculator, rates
 * strip, steps, fee comparison, download, FAQ, CTA. Pair SEO pages and
 * `/send-money-home` both render this so FX UI stays in one place.
 */
export function SendMoneyCalculatorPage({
  heroTitle,
  initialTarget,
}: SendMoneyCalculatorPageProps) {
  // Currency pair / amount / plan are owned here so the rates strip, the
  // calculator, and the comparison table all stay in sync: clicking a strip pair
  // or changing the plan updates the calculator and the table live.
  const [base, setBase] = useState('USD');
  const [target, setTarget] = useState<ReceiveCurrency>(initialTarget);
  const [amount, setAmount] = useState('1000');
  const [plan, setPlan] = useState<PricingTier>('PRO');

  return (
    <PageShell headerTransparentUntilScroll>
      {/* Hero: full-width grey band; copy + features on the left, calculator on the
          right. The calculator sits in grid cols 5–8 (9-col grid) so the white card
          is centered on the 6/7 gridline; on mobile it stacks and centers.

          The header floats transparent over this band (PageShell pulls content up
          by the 64px header via -mt-16), so h1's distance from the top of the page
          equals this pt value. It bundles three things and avoids double-counting:
          the hero's own breathing room (56 / 80), the 64px transparent-header
          clearance, and a +16px extra top padding, minus 24px —
          pt-[112px]  = 56 + 64 + 16 - 24, md:pt-[136px] = 80 + 64 + 16 - 24. */}
      <Section className="bg-grey-50 pt-[112px] pb-16 md:pt-[136px] md:pb-24">
        <Grid className="items-start gap-y-12 lg:items-stretch">
          <div className="col-span-9 flex flex-col lg:col-span-4">
            {/* On desktop the left column stretches to the calculator's height
                (Grid lg:items-stretch); this flex-1 wrapper fills the space above
                the QR and vertically centres the title + feature list within it.
                On mobile it collapses to content height, so the stacked spacing is
                unchanged. */}
            <div className="flex flex-1 flex-col lg:justify-center">
              <h1 className="text-[40px] leading-[1.1] font-semibold tracking-[-0.03em] whitespace-pre-line text-balance md:text-h2">
                {heroTitle}
              </h1>
              {/* Invisible divider: transparent border keeps the exact vertical
                  spacing of the visible rule without drawing a line. */}
              <div className="mt-8 border-t border-transparent" />
              <ul className="mt-8 flex flex-col gap-8">
                {sendMoneyHeroFeatures.map((feature) => (
                  <li key={feature.title} className="flex items-start gap-5">
                    <span className="flex size-12 shrink-0 items-center justify-center rounded-full bg-grey-100 text-ink">
                      <Icon name={feature.icon} className="size-6" />
                    </span>
                    {/* Title/description styling + gap match FeatureBlock's rows below. */}
                    <div className="flex flex-col gap-5">
                      <h3 className="text-body font-semibold">{feature.title}</h3>
                      <p className="text-body text-grey-900">{feature.description}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            {/* QR stays pinned to the bottom (aligned with the 508px calculator
                card) with an 80px gap above it. */}
            <DownloadQr className="mt-20 self-start" />
          </div>
          {/* Extra 24px gap above the calculator on mobile only (net 72px with the
              Grid's gap-y-12); removed at md+ where the QR appears / layout goes
              side-by-side, so desktop spacing is unchanged. */}
          <div className="col-span-9 mt-6 flex justify-center md:mt-0 lg:col-start-5 lg:col-span-4">
            <CurrencyCalculator
              base={base}
              target={target}
              amount={amount}
              plan={plan}
              onBaseChange={setBase}
              onTargetChange={setTarget}
              onAmountChange={setAmount}
              onPlanChange={setPlan}
            />
          </div>
        </Grid>
      </Section>
      <CurrencyRatesStrip onSelect={setTarget} plan={plan} />
      {/* Send easily in 3 steps: 3-card grid (reuses the /compare steps content),
          positioned directly above the Compare fees table. Extra 80px top gap
          (pt-20) between the currency-rates strip and this section. */}
      <div className="pt-[120px] pb-10">
        <StepsSection heading="Send easily in 3 steps" steps={compareSteps} />
      </div>
      {/* Unified comparison table, driven by the calculator's live pair/amount/plan. */}
      <RateComparison
        heading="Compare fees"
        amount={Number.parseFloat(amount) || 0}
        source={base}
        target={target}
        plan={plan}
        showSavings
        onAmountChange={setAmount}
        onSourceChange={setBase}
        onTargetChange={(code) => setTarget(code as ReceiveCurrency)}
      />
      {/* Extra 40px gap (pt-10) between the comparison table and the "Download the
          nsave app" section, consistent with how sections are spaced on this page. */}
      <div className="pt-10">
        <FeatureBlock content={sendMoneyDownload} imagePosition="left" ctaSlot={<DownloadQr />} />
      </div>
      <TextSection content={safeSpaceSection} sectionClassName="max-md:!my-[40px]" />
      <TestimonialGrid />
      <FaqAccordion />
      <CtaBanner />
    </PageShell>
  );
}
